import React, { useCallback, useEffect, useRef, useState } from "react";

export default function ProjectPhotoSlider({
  images = [],
  altPrefix = "Project",
  autoPlayMs = 4800,
  variant = "hero",
  isRtl = false,
  onSlideChange,
  caption,
}) {
  const count = images.length;
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progressTick, setProgressTick] = useState(0);
  const thumbsRef = useRef(null);
  const rootRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const isLightboxOpen = lightboxIndex >= 0 && lightboxIndex < count;
  const showNavArrows = variant !== "showcase" && count > 1;
  const enableLightbox = variant === "showcase";

  const scrollActiveThumb = useCallback(
    (index) => {
      const container = thumbsRef.current;
      if (!container || variant !== "showcase") return;

      const activeThumb = container.querySelector(`[data-thumb-index="${index}"]`);
      if (!activeThumb) return;

      const targetLeft =
        activeThumb.offsetLeft - container.clientWidth / 2 + activeThumb.clientWidth / 2;
      container.scrollTo({ left: Math.max(0, targetLeft), behavior: "smooth" });
    },
    [variant]
  );

  const goTo = useCallback(
    (index, { scrollThumb = false } = {}) => {
      if (!count) return;
      const nextIndex = ((index % count) + count) % count;
      setActive(nextIndex);
      setProgressTick((tick) => tick + 1);
      if (scrollThumb) scrollActiveThumb(nextIndex);
    },
    [count, scrollActiveThumb]
  );

  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  useEffect(() => {
    if (!count || paused || !inView || count < 2) return undefined;
    const timer = window.setInterval(next, autoPlayMs);
    return () => window.clearInterval(timer);
  }, [count, paused, inView, autoPlayMs, next, active]);

  useEffect(() => {
    onSlideChange?.(active, images[active]);
  }, [active, images, onSlideChange]);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.35 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (variant !== "showcase" || !paused) return;
    scrollActiveThumb(active);
  }, [active, paused, variant, scrollActiveThumb]);

  const openLightbox = useCallback(() => {
    if (!enableLightbox) return;
    setLightboxIndex(active);
    setPaused(true);
  }, [active, enableLightbox]);

  const closeLightbox = useCallback(() => {
    setLightboxIndex((current) => {
      if (current >= 0) {
        queueMicrotask(() => goTo(current, { scrollThumb: true }));
      }
      return -1;
    });
  }, [goTo]);

  useEffect(() => {
    if (!isLightboxOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeLightbox();
        return;
      }
      if (count < 2) return;
      if (event.key === "ArrowLeft") {
        setLightboxIndex((current) => (current <= 0 ? count - 1 : current - 1));
      }
      if (event.key === "ArrowRight") {
        setLightboxIndex((current) => (current >= count - 1 ? 0 : current + 1));
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeLightbox, count, isLightboxOpen]);

  if (!count) return null;

  const counterLabel = `${active + 1} / ${count}`;

  return (
    <div
      ref={rootRef}
      className={`dk-slider dk-slider--${variant}${paused ? " is-paused" : ""}`}
      dir={isRtl ? "rtl" : "ltr"}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div
        className={`dk-slider__stage${enableLightbox ? " dk-slider__stage--zoomable" : ""}`}
        aria-live="polite"
        onClick={enableLightbox ? openLightbox : undefined}
        onKeyDown={
          enableLightbox
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openLightbox();
                }
              }
            : undefined
        }
        role={enableLightbox ? "button" : undefined}
        tabIndex={enableLightbox ? 0 : undefined}
        aria-label={enableLightbox ? (isRtl ? "عرض الصورة بالحجم الكامل" : "View full-size photo") : undefined}
      >
        {images.map((src, index) => (
          <div
            key={`${src}-${index}`}
            className={`dk-slider__slide${index === active ? " is-active" : ""}`}
            aria-hidden={index !== active}
          >
            <img src={src} alt={`${altPrefix} ${index + 1}`} loading={index <= 1 ? "eager" : "lazy"} draggable={false} />
          </div>
        ))}

        {showNavArrows ? (
          <>
            <button
              type="button"
              className="dk-slider__nav dk-slider__nav--prev"
              onClick={(event) => {
                event.stopPropagation();
                prev();
              }}
              aria-label="Previous photo"
            >
              ‹
            </button>
            <button
              type="button"
              className="dk-slider__nav dk-slider__nav--next"
              onClick={(event) => {
                event.stopPropagation();
                next();
              }}
              aria-label="Next photo"
            >
              ›
            </button>
          </>
        ) : null}

        <div className="dk-slider__top-meta">
          <span className="dk-slider__counter">{counterLabel}</span>
          {paused ? <span className="dk-slider__paused">{isRtl ? "متوقف" : "Paused"}</span> : null}
        </div>

        {enableLightbox ? (
          <span className="dk-slider__zoom-hint" aria-hidden="true">
            {isRtl ? "اضغط للتكبير" : "Tap to enlarge"}
          </span>
        ) : null}

        {caption ? (
          <div className="dk-slider__caption">
            <span>{caption.primary}</span>
            <strong>{caption.secondary}</strong>
          </div>
        ) : null}
      </div>

      {count > 1 ? (
        <>
          <div className="dk-slider__progress" key={progressTick}>
            <span style={{ animationDuration: `${autoPlayMs}ms` }} />
          </div>

          {variant === "showcase" ? (
            <div className="dk-slider__thumbs" ref={thumbsRef} role="tablist" aria-label="Photo thumbnails">
              {images.map((src, index) => (
                <button
                  key={`thumb-${src}-${index}`}
                  type="button"
                  data-thumb-index={index}
                  role="tab"
                  aria-selected={index === active}
                  aria-label={`Photo ${index + 1}`}
                  className={`dk-slider__thumb${index === active ? " is-active" : ""}`}
                  onClick={() => goTo(index, { scrollThumb: true })}
                >
                  <img src={src} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          ) : (
            <div className="dk-slider__dots dk-slider__dots--compact" role="tablist" aria-label="Photo navigation">
              {images.map((src, index) => (
                <button
                  key={`dot-${src}-${index}`}
                  type="button"
                  role="tab"
                  aria-selected={index === active}
                  aria-label={`Photo ${index + 1}`}
                  className={index === active ? "is-active" : ""}
                  onClick={() => goTo(index)}
                />
              ))}
            </div>
          )}
        </>
      ) : null}

      {isLightboxOpen ? (
        <div className="qtech-property-lightbox" role="dialog" aria-modal="true" onClick={closeLightbox}>
          <button type="button" className="qtech-property-lightbox-close" onClick={closeLightbox} aria-label="Close image">
            ×
          </button>
          {count > 1 ? (
            <button
              type="button"
              className="qtech-property-lightbox-nav qtech-property-lightbox-nav-prev"
              onClick={(event) => {
                event.stopPropagation();
                setLightboxIndex((current) => (current <= 0 ? count - 1 : current - 1));
              }}
              aria-label="Previous image"
            >
              ‹
            </button>
          ) : null}
          <div className="qtech-property-lightbox-stage" onClick={(event) => event.stopPropagation()}>
            <img
              className="qtech-property-lightbox-image"
              src={images[lightboxIndex]}
              alt={`${altPrefix} ${lightboxIndex + 1}`}
            />
            <div className="qtech-property-lightbox-caption">
              {count > 1 ? `${lightboxIndex + 1} / ${count}` : null}
            </div>
          </div>
          {count > 1 ? (
            <button
              type="button"
              className="qtech-property-lightbox-nav qtech-property-lightbox-nav-next"
              onClick={(event) => {
                event.stopPropagation();
                setLightboxIndex((current) => (current >= count - 1 ? 0 : current + 1));
              }}
              aria-label="Next image"
            >
              ›
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
